import { IField, IFieldOption, SelectOptionProps } from '@/client/types'
import { IOrderTemplate } from '@/client/types/OrderTemplate'

// Functions that are used in Calculator already and passed as reference to some other functions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IsDisabledFunction = (disableList: any) => boolean
type HandleDisabledSelections = (fieldKey: string, optionKey: string) => boolean
type HandleSelect = (optionId, value, relatedFieldId, relatedField, key) => void

/**
 * Finds an option by parent key e.g. "label_type" and option ID, and returns the whole option with relatedField, relatedFieldId etc.
 * @param data - The JSON data containing the field and options.
 * @param parentKey - The key of the parent field (e.g., "label_type").
 * @param optionId - The ID of the option to search for.
 * @returns The relatedField or relatedFieldId if found, otherwise undefined.
 */
function findSelectedOption(data: IField[], parentKey: string, optionId: string): { option: IFieldOption } | undefined {
  // Find the field by key like "key": "label_type"
  const field = data.find((f) => f.key === parentKey)

  if (field == undefined) {
    return undefined // Field not found
  }

  // Find the option of that field by id
  const option = field.options.find((option) => option.id == optionId)

  if (option === undefined) {
    return undefined // Option ID not found
  }

  return {
    option
  }
}

// We have the handleSelectLight to use a simpler component like drop down that can just pass a key and value to the change handler.
// Therefore, we need to enrich the parameters with data from the fields like relatedFieldId/relatedField/key etc.
export const handleSelectKeyValue = (fieldKey, value, fields: IField[], handleSelect: HandleSelect) => {
  // Try to convert the string value to a number value
  // For some controls even they pass a string its automatically converted (unresolved mystery:-)
  const testNumberConversion = parseInt(value)
  if (!isNaN(testNumberConversion)) {
    value = testNumberConversion
  }
  const { option } = findSelectedOption(fields, fieldKey, value) || {}

  handleSelect(fieldKey, value, option?.relatedFieldId, option?.relatedField, option?.key)
}

/**
 * Get filtered options defined by the order template for a given field (value_positive_list).
 * It also takes into account that options are disabled by other options. Here we either remove the option from the
 * option list or disable it (controlled by excludeDisabledOptions).
 *
 * @param field
 * @param excludeDisabledOptions - if true, options that are disabled (by the list of other options) will be excluded
 * from the list, otherwise they will be set as disabled
 * @param template
 * @param isDisabled a reference to the isDisabled function from the calculator
 * @param handleDisabledSelections a reference to the handleDisabledSelections function from the calculator
 * @aparam isDisabled: IsDisabledFunction from calculator
 * @returns the filtered options to be used in a select component
 */
export const getTemplateFilteredOptions = (
  field: IField,
  excludeDisabledOptions: boolean = false,
  template: IOrderTemplate | undefined,
  isDisabled?: IsDisabledFunction,
  handleDisabledSelections?: HandleDisabledSelections
): SelectOptionProps[] =>
  field?.options
    ?.filter(({ id, key, disabled }) => {
      // Values that are disabled will be excluded rather than disabled
      if (excludeDisabledOptions) {
        if (isDisabled && isDisabled(disabled)) {
          return false
        }
        // Also use this helper to exclude an option from a select rather than disabling it.
        if (handleDisabledSelections && handleDisabledSelections(field.key, key)) {
          return false
        }
      }

      // Related to REL-776 Filter out options that you do not want to see
      const valuePositiveList = template?.[field.key]?.value_positive_list

      if (!valuePositiveList?.length) {
        return true
      }

      return valuePositiveList.map(String).includes(String(id))
    })
    .map((option) => ({
      // Add the price variant to the option if exists like (+ 30.00 EUR)
      ...option,
      name: option.priceVariant ? `${option.name} (${option.priceVariant})` : `${option.name}`
    }))
    .map((option) => ({
      // Transform IFieldOption to SelectOptionProps
      // We use disabled here on option meaning it will be shown but not selectable
      id: option.id,
      name: option.name,
      disabled:
        (isDisabled && isDisabled(option.disabled)) ||
        (handleDisabledSelections && handleDisabledSelections(field.key, option.key))
    }))
